// src/pages/SuperAdmin/AdminUsuarios.tsx
import React, { useEffect, useState, useCallback } from "react";
import { fetchUsuarios } from "../../services/adminApi";
import { Search, Agriculture } from "@mui/icons-material";

type Fazenda = { uid_unidade_producao: string; nome: string; estado: string };
type Usuario = {
  uid_usuario: string;
  nome: string;
  email: string | null;
  telefone: string | null;
  modulo: string;
  ativo: number;
  syn_cadmom: string;
  fazenda: Fazenda | null;
};

const moduloColor: Record<string, { bg: string; color: string }> = {
  agro:  { bg: "#e8f4ef", color: "#00b37e" },
  pesca: { bg: "#eff6ff", color: "#3b82f6" },
  admin: { bg: "#fef3c7", color: "#d97706" },
};

const formatTelefone = (t: string | null) => {
  if (!t) return "—";
  const d = t.replace(/\D/g, "");
  if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t;
};

const formatData = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

const AdminUsuarios2: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtroModulo, setFiltroModulo] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const carregar = useCallback(() => {
    setLoading(true);
    fetchUsuarios(debouncedSearch, filtroModulo)
      .then((data) => setUsuarios(
        [...data].sort((a: Usuario, b: Usuario) =>
          new Date(b.syn_cadmom).getTime() - new Date(a.syn_cadmom).getTime()
        )
      ))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [debouncedSearch, filtroModulo]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div style={{ padding: "32px 28px", maxWidth: 1100, width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 700, color: "#1a2e1e" }}>
          Usuários Cadastrados
        </h1>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.9rem" }}>
          {loading ? "Carregando..." : `${usuarios.length} usuário(s) encontrado(s)`}
        </p>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "#fff", border: "1px solid #d1d5db",
          borderRadius: 10, padding: "8px 14px", flex: 1, minWidth: 200,
        }}>
          <Search style={{ color: "#9ca3af", fontSize: 20 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, email ou telefone..."
            style={{ border: "none", outline: "none", fontSize: "0.9rem", width: "100%", background: "transparent" }}
          />
        </div>
        <select
          value={filtroModulo}
          onChange={(e) => setFiltroModulo(e.target.value)}
          style={{
            padding: "8px 14px", borderRadius: 10, border: "1px solid #d1d5db",
            fontSize: "0.9rem", background: "#fff", cursor: "pointer",
          }}
        >
          <option value="">Todos os módulos</option>
          <option value="agro">Agro</option>
          <option value="pesca">Pesca</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {/* Tabela */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5ede8", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Carregando...</div>
        ) : usuarios.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Nenhum usuário encontrado.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8faf9", borderBottom: "1px solid #e5ede8" }}>
                  {["Nome", "Email / Telefone", "Módulo", "Fazenda", "Cadastro", "Ativo"].map((h) => (
                    <th key={h} style={{
                      padding: "12px 16px", textAlign: "left",
                      fontSize: "0.75rem", fontWeight: 700,
                      color: "#374151", textTransform: "uppercase", letterSpacing: 0.5,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => {
                  const mc = moduloColor[u.modulo] || moduloColor.agro;
                  return (
                    <tr key={u.uid_usuario} style={{ borderBottom: "1px solid #f3f4f6" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8faf9")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                    >
                      <td style={{ padding: "14px 16px", fontWeight: 600, color: "#1a2e1e", fontSize: "0.9rem" }}>
                        {u.nome}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#6b7280", fontSize: "0.85rem" }}>
                        <div>{u.email || "—"}</div>
                        <div>{formatTelefone(u.telefone)}</div>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{
                          background: mc.bg, color: mc.color,
                          borderRadius: 6, padding: "3px 10px",
                          fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase",
                        }}>
                          {u.modulo}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", color: "#374151", fontSize: "0.85rem" }}>
                        {u.fazenda ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Agriculture style={{ fontSize: 16, color: "#00b37e" }} />
                            <span>{u.fazenda.nome} — {u.fazenda.estado}</span>
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>Sem vínculo</span>
                        )}
                      </td>
                      <td style={{ padding: "14px 16px", color: "#6b7280", fontSize: "0.85rem" }}>
                        {formatData(u.syn_cadmom)}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{
                          background: u.ativo ? "#e8f4ef" : "#fee2e2",
                          color: u.ativo ? "#00b37e" : "#ef4444",
                          borderRadius: 6, padding: "3px 10px",
                          fontSize: "0.75rem", fontWeight: 700,
                        }}>
                          {u.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsuarios2;